import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { IoSearchOutline, IoFilterOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";

import DebounceInput from "../../../../components/DebounceInput";
import MultiAction from "../../../../components/MultiAction";
import MultiSelect from "../../../../components/MultiSelect";
import SelectUser from "../../../../components/selectUser";

import api from "../../../../services/api";

import Kanban from "./components/kanban";
import List from "./components/list";

import NotionModal from "./components/notionModal";

import BulkActionModal from "./components/BulkActionsModal";

import NotionStats from "./components/Stats";
import History from "./components/history";
import Reports from "./components/Reports";

import Loader from "../../../../components/loader";
import DatePicker from "../../../../components/DatePicker";

export default ({ project, setProject }) => {
  const [notions, setNotions] = useState([]);

  const [filters, setFilters] = useState({
    page: 1,
    startDate: null,
    endDate: null,
    sort: { created_at: -1 },
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE"],
  });

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState();
  const [budgets, setBudgets] = useState([]);
  const [openModalMultiSelect, setOpenModalMultiSelect] = useState(false);
  const [openReport, setOpenReport] = useState(false);

  const [selectedNotions, setSelectedNotions] = useState([]);

  const { search } = useLocation();
  const navigate = useNavigate();

  const { id, tab } = useParams();

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  useEffect(() => {
    if (tab === "kanban") return setView("KANBAN");
    if (tab === "list") return setView("LIST");
    if (tab === "stats") return setView("STATS");
    if (tab === "history") return setView("HISTORY");
    if (tab === "report") return setView("REPORT");
    setView("KANBAN");
  }, [tab]);

  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    fetch();
  }, [filters]);

  useEffect(() => {
    getBudgets();
  }, []);

  const getBudgets = async () => {
    try {
      const { data } = await api.post("/budget/search", { projectId: project._id, status: "active" });
      setBudgets(data);
    } catch (e) {
      console.log("e", e);
    }
  };

  async function fetch() {
    setLoading(true);
    const newFilters = { ...filters, project_id: project._id };
    const { data, ok } = await api.post("/notion/search", newFilters);
    if (!ok) return toast.error("Error fetching users");

    setNotions(data);
    setLoading(false);
    // setSelectedNotions(data.reduce((acc, notion) => ({ ...acc, [notion._id]: false }), {}));
  }

  async function onExport() {
    const newFilters = { ...filters, project_id: project._id };
    const { data: notions, ok } = await api.post("/notion/search", newFilters);
    if (!ok) return toast.error("Error fetching users");

    if (!notions.length) return;

    let arr = notions.map((notion) => {
      console.log("notion", notion);
      // Format comments array into readable text with HTML stripped
      const commentsText =
        notion.comments
          ?.filter((comment) => comment.text) // Only include comments with text
          ?.map((comment) => {
            const date = new Date(comment.created_at).toLocaleDateString();
            // Strip HTML tags and decode HTML entities
            const cleanText = comment.text
              ?.replace(/<[^>]*>/g, "") // Remove HTML tags
              ?.replace(/&nbsp;/g, " ") // Replace &nbsp; with space
              ?.replace(/&amp;/g, "&") // Replace &amp; with &
              ?.replace(/&lt;/g, "<") // Replace &lt; with <
              ?.replace(/&gt;/g, ">") // Replace &gt; with >
              ?.replace(/&quot;/g, '"') // Replace &quot; with "
              ?.trim();

            return `${comment.user_name || "Unknown"} (${date}):\n${cleanText}\n----------------------------------------`; // Separator line
          })
          .join("\n\n") || ""; // Double newline between comments

      return {
        name: notion.name,
        priority: (notion.priority || "").toUpperCase(),
        category: notion.category,
        status: notion.status,
        budget: budgets.find((b) => b._id === notion.budget_id)?.name,
        time: notion.estimated_hours,
        price: notion.estimated_price,
        link: `https://accounting.selego.co/project/662e7b0607f5bf070d91916e/backlog?task_modal_id=${notion._id}`,
        created_at: notion.created_at,
        comments: commentsText,
      };
    });

    const date = new Date();
    const filename = `${project.name} - ${date.toLocaleDateString("fr")}.csv`;
    const header = Object.keys(arr[0]);

    // Format CSV with proper escaping for comments
    const csv = [header.join(";"), ...arr.map((row) => header.map((field) => `"${(row[field] || "").toString().replace(/"/g, '""')}"`).join(";"))].join("\n");

    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }); // Add BOM for Excel UTF-8 support
    if (window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const encodedUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUrl);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(encodedUrl);
    }
  }

  const actions = [];
  if (selectedNotions.length > 0) actions.push({ name: "Edit", cb: () => setOpenModalMultiSelect(true) });
  if (selectedNotions.length > 0) actions.push({ name: "Export", cb: onExport });

  return (
    <div className="bg-white border border-gray-300 rounded-md overflow-hidden px-4 py-2">
      <div className="bg-white h-full">
        <div className="bg-white rounded-lg mb-2">
          <div className="p-3 flex items-center gap-3 border-b border-gray-100">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-4 w-4 text-gray-400" />
              </div>
              <DebounceInput
                debounce={300}
                className="block w-full pl-10 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <div className="grid grid-cols-3 gap-2">
                <div className="w-44">
                  <MultiSelect
                    id="select-status"
                    options={["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE", "ARCHIVED"].map((status) => ({ value: status, label: status }))}
                    onSelectedChange={(e) => setFilters((f) => ({ ...f, statuses: e.map((e) => e.value) }))}
                    values={filters.statuses.map((status) => ({ value: status, label: status }))}
                    placeholder="Status"
                    buttonClassName="px-3 py-1.5"
                  />
                </div>
                <div className="w-44">
                  <MultiSelect
                    id="select-category"
                    options={["BUG", "FEATURE"].map((category) => ({ value: category, label: category }))}
                    onSelectedChange={(e) => setFilters((f) => ({ ...f, categories: e.map((e) => e.value) }))}
                    placeholder="Category"
                    buttonClassName="px-3 py-1.5"
                  />
                </div>
                <div className="w-44">
                  <MultiSelect
                    id="select-priority"
                    options={["P0", "P1", "P2", "P3"].map((priority) => ({ value: priority, label: priority }))}
                    onSelectedChange={(e) => setFilters((f) => ({ ...f, priorities: e.map((e) => e.value) }))}
                    placeholder="Priority"
                    buttonClassName="px-3 py-1.5"
                  />
                </div>
              </div>

              <button
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md transition-colors
                ${showMoreFilters ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>
                <IoFilterOutline className="h-4 w-4" />
                More Filters
              </button>

              <div className="flex items-center gap-2 pl-2 border-l border-gray-200">
                <MultiAction
                  actions={[
                    {
                      name: "New ticket",
                      cb: async () => {
                        const nf = { project_id: project._id };
                        const { data } = await api.post("/notion", nf);
                        const query = new URLSearchParams(search);
                        query.set("task_modal_id", data._id);
                        navigate({ search: query.toString() });
                        setNotions([data, ...notions]);
                      },
                    },
                    {
                      name: "Generate and send report",
                      cb: async () => {
                        navigate(`/project/${project._id}/backlog/report`);
                      },
                    },
                    ...actions,
                  ]}
                />
              </div>
            </div>
          </div>

          <AnimatePresence>
            {showMoreFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="border-t border-gray-100">
                <div className="p-3 grid grid-cols-5 gap-2">
                  <SelectTags project={project} value={filters.tags} onChange={(e) => setFilters({ ...filters, tags: e })} />
                  <SelectModule project={project} value={filters.module} onChange={(e) => setFilters((f) => ({ ...f, module: e }))} />
                  <SelectEpic project={project} value={filters.epic_ids} onChange={(e) => setFilters((f) => ({ ...f, epic_ids: e }))} />
                  <SelectUser
                    name="people"
                    placeholder="User"
                    onChange={(e) => setFilters({ ...filters, user_id: e?._id, user_name: e?.name, user_avatar: e?.avatar })}
                    value={{ _id: filters.user_id, name: filters.user_name, avatar: filters.user_avatar }}
                    buttonClassName="px-3 py-1.5 rounded-md border-gray-200"
                  />
                  <DatePicker value={filters} onChange={setFilters} buttonClassName="px-3 py-1.5 rounded-md border-gray-200" position="top-full right-0" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex justify-between">
          <div className="flex items-end ml-2 ">
            <div onClick={() => navigate(`/project/${id}/backlog/kanban`)} className="flex items-center cursor-pointer">
              <div className={`${view === "KANBAN" ? "gray-btn" : "transparent-btn"} h-8 text-center transition duration-300 rounded-b-none`}>Kanban</div>
            </div>
            <div onClick={() => navigate(`/project/${id}/backlog/list`)} className="flex items-center cursor-pointer">
              <div className={`${view === "LIST" ? "gray-btn" : "transparent-btn"} h-8 text-center transition duration-300 rounded-b-none`}>List</div>
            </div>
            <div onClick={() => navigate(`/project/${id}/backlog/stats`)} className="flex items-center cursor-pointer">
              <div className={`${view === "STATS" ? "gray-btn" : "transparent-btn"} h-8 text-center transition duration-300 rounded-b-none`}>Stats</div>
            </div>
            <div onClick={() => navigate(`/project/${id}/backlog/history`)} className="flex items-center cursor-pointer">
              <div className={`${view === "HISTORY" ? "gray-btn" : "transparent-btn"} h-8 text-center transition duration-300 rounded-b-none`}>History</div>
            </div>
            <div onClick={() => navigate(`/project/${id}/backlog/report`)} className="flex items-center cursor-pointer">
              <div className={`${view === "REPORT" ? "gray-btn" : "transparent-btn"} h-8 text-center transition duration-300 rounded-b-none`}>Report</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <Loader />
          </div>
        ) : view === "KANBAN" ? (
          <Kanban notions={notions} setNotions={setNotions} projectId={project._id} getNotions={fetch} />
        ) : view === "LIST" ? (
          <List notions={notions} setNotions={setNotions} projectId={project._id} getNotions={fetch} selectedNotions={selectedNotions} setSelectedNotions={setSelectedNotions} />
        ) : view === "STATS" ? (
          <NotionStats data={notions} />
        ) : view === "HISTORY" ? (
          <History data={notions} />
        ) : view === "REPORT" ? (
          <Reports notions={notions} selectedNotions={selectedNotions} setSelectedNotions={setSelectedNotions} project={project} />
        ) : null}
      </div>

      <NotionModal
        project={project}
        onDelete={(notion) => setNotions(notions.filter((n) => n._id !== notion._id))}
        onUpdate={(notion) => setNotions(notions.map((n) => (n._id === notion._id ? { ...n, ...notion } : n)))}
      />
      <BulkActionModal selectedNotions={selectedNotions} isOpen={openModalMultiSelect} onClose={() => setOpenModalMultiSelect(false)} projectId={project._id} />
    </div>
  );
};

const SelectTags = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    get();
  }, []);

  async function get() {
    const { data } = await api.post("/notion/aggregate", { project_id: project._id, key: "tags" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
  }

  return (
    <MultiSelect id="select-status" options={options} value={value} onSelectedChange={(e) => onChange(e.map((e) => e.value))} placeholder="Tags" buttonClassName="px-3 py-1.5" />
  );
};

const SelectModule = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);
  const [lastFetch, setLastFetch] = useState(0);

  async function fetchModules() {
    const { data } = await api.post("/notion/aggregate", { project_id: project._id, key: "module" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
    setLastFetch(Date.now());
  }

  useEffect(() => {
    // Initial fetch when component mounts
    fetchModules();
  }, [project._id]);

  // Refetch when component gains focus if more than 5 seconds have passed
  useEffect(() => {
    function handleFocus() {
      if (Date.now() - lastFetch > 5000) {
        fetchModules();
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastFetch]);

  return (
    <MultiSelect
      id="select-modules"
      options={options}
      values={value?.map((tag) => ({ value: tag, label: tag }))}
      onSelectedChange={(e) => onChange(e.map((e) => e.value))}
      placeholder="Module"
      buttonClassName="px-3 py-1.5"
    />
  );
};

const SelectEpic = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);
  const [lastFetch, setLastFetch] = useState(0);

  async function fetchEpics() {
    const { data } = await api.post("/epic/search", { project_id: project._id, sort: "title" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e.title, value: e._id }));
    setOptions(arr);
    setLastFetch(Date.now());
  }

  useEffect(() => {
    // Initial fetch when component mounts
    fetchEpics();
  }, [project._id]);

  // Refetch when component gains focus if more than 5 seconds have passed
  useEffect(() => {
    function handleFocus() {
      if (Date.now() - lastFetch > 5000) {
        fetchEpics();
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastFetch]);

  return (
    <MultiSelect
      id="select-epics"
      options={options}
      values={value?.map((tag) => ({ value: tag, label: tag }))}
      onSelectedChange={(e) => onChange(e.map((e) => e.value))}
      placeholder="Epic"
      buttonClassName="px-3 py-1.5"
    />
  );
};
